import type { ContextContentItemsListProps } from './interfaces';
import { BulkheadRejectedError, Policy } from 'cockatiel';
import { useAttachments } from 'Components/Chat/hooks/attachments/useAttachments';
import { LOAD_DIRECTION } from 'Constants/enums';
import { IManageLastScrollAndBackfill } from 'Interfaces/components';
import { get } from 'lodash';
import { toJS } from 'mobx';
import { ILastScroll } from 'Models/ConversationLastScroll';
import { GroupedMessagesContainer } from 'Models/GroupedMessagesContainer';
import * as React from 'react';
import { useLocation } from 'react-router';
import useStayScrolled from 'react-stay-scrolled';
import { getCurrentConversationId } from 'Utils/getCurrentConversationId';
import { bugsnagClient } from 'Utils/logUtils';
import ScrollToBottomFAB from '../ScrollToBottomFAB';
import { ContextContentItemsGroup } from './ContextContentItemsGroup';
import { useAutoScrollReaction } from './hooks/useAutoScrollReaction';
import { Styled } from './index.styles';

/**
 * **NOT AN OBSERVER.** This is intentional, it is designed to be a dumb component to support React Hooks.
 * @param props
 */
export function ContextContentItemsList(props: ContextContentItemsListProps) {
  const {
    assignLastScroll,
    callWithPerson,
    curConversation,
    deleteMessage,
    editMessage,
    editMessageDraftHtml,
    editMessageDraftRaw,
    getCurrentMessages,
    getEmojiPickerState,
    getOrConstructConversationLastScroll,
    getPerson,
    isBackfillingToReadMessage,
    isEditingMessageId,
    ldClient,
    lastReadMessageId,
    loadLinkPreview,
    loadMoreConversationMessages,
    loggedInPersonId,
    loggedInUserActiveConferenceConversation,
    mentionListOpen,
    messages,
    messagesWithLinkPreviewHeightCount,
    messageMentionFilter,
    navigateToConferenceByConfId,
    newestMessageOwnedByUserId,
    resolveConversationLinkPath,
    selectedMentionParticipantId,
    selectFilteredOtherParticipantPersonsInCurrentConversation,
    selectHasRecentActivityWithin,
    selectCanLoadOlderMessages,
    selectLoadMoreMessagesSkipIds,
    selectMarkedAsReadInfo,
    selectEpochMsSinceMarkedAsRead,
    selectParticipantPersons,
    selectPersonMessageStatus,
    selectPersonPresenceStatus,
    selectUnreadCounts,
    setConversationAndTotalUnreadCount,
    setEditMessageDraftHtml,
    setEditMessageDraftRaw,
    setEmojiPickerState,
    setIsEditingMessageId,
    setIsLoadingOlderMessages,
    setMarkedAsReadInfo,
    setMentionListOpen,
    setMessageMentionFilter,
    setSelectedMentionParticipantId,
    sortedMessageGroups,
    totalLinkPreviewHeightSum,
    totalMessageCount,
    totalUnreadCount,
    updateMyLastReadMessage,
    loadPersonByIdGetIfMissingGet,
    listOfPinnedMessages,
    selectParticipantsByConversationId,
    conversationStore,
    getFileDownloadLink,
    handleDownloadWithLink,
    ui,
    auth0,
  } = props;

  const { pathname } = useLocation();
  const { getAttachmentsCount } = useAttachments();

  const conversationId = React.useMemo(
    () => getCurrentConversationId(pathname),
    [pathname]
  );

  const listRef = React.useRef<HTMLDivElement>();
  const [initialized, setInitialized] = React.useState<boolean>(false);
  const [lastMessageCount, setLastMessageCount] =
    React.useState<number>(totalMessageCount);
  const [lastUnreadCount, setLastUnreadCount] =
    React.useState<number>(totalUnreadCount);
  const [lastLinkPreviewHeightSum, setLastLinkPreviewHeightSum] =
    React.useState<number>(totalLinkPreviewHeightSum);
  const [
    lastMessagesWithLinkPreviewHeightCount,
    setLastMessagesWithLinkPreviewHeightCount,
  ] = React.useState<number>(messagesWithLinkPreviewHeightCount);
  const initCLS = toJS(getOrConstructConversationLastScroll(conversationId));
  const [scrolledToBottomOnFirstLoad, setScrolledToBottomOnFirstLoad] =
    React.useState<boolean>(initCLS.scrollHeight !== 0);
  const userIsScrolling = React.useRef<boolean>(false);
  // Tracks the newest message id so the layout effect can tell an append at the
  // bottom apart from a prepend at the top
  const lastNewestMessageId = React.useRef<string | undefined>(
    messages?.NewestMessageId
  );
  const { scroll, scrollBottom, stayScrolled, isScrolled } = useStayScrolled(
    listRef,
    { initialScroll: initCLS.scrollTop || Infinity, inaccuracy: 250 }
  );

  const onScroll = React.useCallback(() => {
    void onCCILScrollBulkhead(
      listRef,
      {
        assignLastScroll,
        conversationId,
        getCurrentMessages,
        getOrConstructConversationLastScroll,
        loadMoreConversationMessages,
        isBackfillingToReadMessage,
        selectLoadMoreMessagesSkipIds,
        selectCanLoadOlderMessages,
        setIsLoadingOlderMessages,
        lastUnreadCount,
        scrolledToBottomOnFirstLoad,
        userIsScrolling,
      },
      scroll
    );
  }, [lastUnreadCount, scrolledToBottomOnFirstLoad]);
  // Typically you will want to use stayScrolled or scrollBottom inside
  // useLayoutEffect, because it measures and changes DOM attributes (scrollTop) directly
  React.useLayoutEffect(() => {
    const conversationLastScroll = toJS(
      getOrConstructConversationLastScroll(conversationId)
    );
    // if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
    //     console.debug('unread/totalMessage layoutEffect ', JSON.stringify({ initialized, lastUnreadCount, lastMessageCount }), 'totalUnreadCount', totalUnreadCount, 'totalMessageCount', totalMessageCount, `|| conversationLastScroll ${JSON.stringify(conversationLastScroll)} || initialRefScroll ${JSON.stringify(initialRefScroll)}`);
    // }
    // Initialize scroll position on mount
    if (!initialized) {
      if (totalUnreadCount === 0 && conversationLastScroll.scrollHeight === 0) {
        setTimeout(() => {
          // console.debug('layout initialize scrollBottom');
          try {
            scrollBottom();
            assignLastScroll(conversationId, {
              clientHeight: listRef.current.clientHeight,
              scrollHeight: listRef.current.scrollHeight,
              scrollTop: listRef.current.scrollTop,
            });
            setInitialized(true);
          } catch (e) {
            // just ignore the exception, this happens when switching back and forth quickly between conversations
            // this is due to the fact that this runs on the next frame, and the component might have been unmounted
          }
        });
      }
      // Already had a `conversationLastScroll` entry
      else if (
        totalUnreadCount === 0 &&
        conversationLastScroll.scrollHeight > 0
      ) {
        setTimeout(() => {
          // console.debug('layout had lastscroll next scrollTop', conversationLastScroll.scrollTop);
          try {
            scroll(conversationLastScroll.scrollTop);
            assignLastScroll(conversationId, {
              clientHeight: listRef.current.clientHeight,
              scrollHeight: listRef.current.scrollHeight,
              scrollTop: listRef.current.scrollTop,
            });
            setInitialized(true);
          } catch (e) {
            // just ignore the exception, this happens when switching back and forth quickly between conversations
            // this is due to the fact that this runs on the next frame, and the component might have been unmounted
          }
        });
      } else {
        // console.debug('layout default initialize with unreads');
        setInitialized(true);
      }
    }
    // Handle changes to unread/message count, link preview height sum
    else {
      // A genuine append at the bottom changes the newest message id. Prepending older messages
      // (infinite scroll) grows totalMessageCount but leaves the newest id untouched
      const newestMessageChanged =
        messages?.NewestMessageId !== lastNewestMessageId.current;
      if (
        (totalMessageCount > lastMessageCount && newestMessageChanged) ||
        totalUnreadCount < lastUnreadCount
      ) {
        //  && newestMsgId === newestOwnedMsgId
        setTimeout(() => {
          console.debug(
            'layout stayScrolled',
            'totalMessageCount > lastMessageCount',
            totalMessageCount > lastMessageCount,
            '(totalUnreadCount < lastUnreadCount)',
            totalUnreadCount < lastUnreadCount
          );
          try {
            stayScrolled();
            assignLastScroll(conversationId, {
              clientHeight: listRef.current.clientHeight,
              scrollHeight: listRef.current.scrollHeight,
              scrollTop: listRef.current.scrollTop,
            });
          } catch (e) {
            // just ignore the exception, this happens when switching back and forth quickly between conversations
            // this is due to the fact that this runs on the next frame, and the component might have been unmounted
          }
        });
      }
      // Scroll down by the difference between the previous and current linkPreviewHeightSum
      else if (
        totalLinkPreviewHeightSum > lastLinkPreviewHeightSum &&
        totalUnreadCount === 0
      ) {
        setTimeout(() => {
          const heightDelta =
            totalLinkPreviewHeightSum - lastLinkPreviewHeightSum;
          const heightCountDelta =
            messagesWithLinkPreviewHeightCount -
            lastMessagesWithLinkPreviewHeightCount;

          // console.debug('layout scroll down by', heightDelta, 'totalLinkPreviewHeightSum > lastLinkPreviewHeightSum', totalLinkPreviewHeightSum, lastLinkPreviewHeightSum);
          try {
            scroll(
              conversationLastScroll.scrollTop +
                heightDelta +
                20 * heightCountDelta
            ); // Add 20px for top/bottom margin of each LinkPreview
            assignLastScroll(conversationId, {
              clientHeight: listRef.current.clientHeight,
              scrollHeight: listRef.current.scrollHeight,
              scrollTop: listRef.current.scrollTop,
            });
            setLastLinkPreviewHeightSum(totalLinkPreviewHeightSum);
            setLastMessagesWithLinkPreviewHeightCount(
              messagesWithLinkPreviewHeightCount
            );
          } catch (e) {
            // just ignore the exception, this happens when switching back and forth quickly between conversations
            // this is due to the fact that this runs on the next frame, and the component might have been unmounted
          }
        });
      }
    }

    setLastMessageCount(totalMessageCount);
    setLastUnreadCount(totalUnreadCount);
    lastNewestMessageId.current = messages?.NewestMessageId;
    // otherwise, let the MessageGroup scroll to its unread divider ref
  }, [
    totalUnreadCount,
    totalMessageCount,
    totalLinkPreviewHeightSum,
    messagesWithLinkPreviewHeightCount,
    messages,
    stayScrolled,
    assignLastScroll,
    conversationId,
  ]);

  const pixelsScrollBottom = React.useRef<number | undefined>();
  if (listRef.current) {
    pixelsScrollBottom.current =
      listRef.current.scrollHeight -
      listRef.current.scrollTop -
      listRef.current.clientHeight;
  }

  React.useEffect(() => {
    if (
      pixelsScrollBottom.current !== undefined &&
      listRef.current &&
      getAttachmentsCount > 0
    ) {
      listRef.current.scrollTo({
        top:
          listRef.current.scrollHeight -
          listRef.current.clientHeight -
          pixelsScrollBottom.current,
        // @ts-ignore
        behavior: 'instant',
      });
    }
  }, [getAttachmentsCount]);

  useAutoScrollReaction({
    enabled: initialized,
    newestMessageId: messages?.NewestMessageId,
    scrollContainerRef: listRef,
  });

  const ccigNodes: React.ReactNode[] = [];
  sortedMessageGroups.forEach((mg, groupIndex) => {
    ccigNodes.push(
      <ContextContentItemsGroup
        auth0={auth0}
        setFileDeletePopup={ui.setFileDeletePopup}
        handleDownloadWithLink={handleDownloadWithLink}
        getFileDownloadLink={getFileDownloadLink}
        listOfPinnedMessages={listOfPinnedMessages}
        group={mg}
        key={mg.groupKey}
        totalUnreadCount={totalUnreadCount}
        callWithPerson={callWithPerson}
        conversationId={conversationId}
        curConversation={curConversation}
        deleteMessage={deleteMessage}
        editMessage={editMessage}
        editMessageDraftHtml={editMessageDraftHtml}
        editMessageDraftRaw={editMessageDraftRaw}
        getEmojiPickerState={getEmojiPickerState}
        getPerson={getPerson}
        groupIndex={groupIndex}
        isEditingMessageId={isEditingMessageId}
        isScrolled={isScrolled}
        loadLinkPreview={loadLinkPreview}
        lastReadMessageId={lastReadMessageId}
        loggedInPersonId={loggedInPersonId}
        loggedInUserActiveConferenceConversation={
          loggedInUserActiveConferenceConversation
        }
        mentionListOpen={mentionListOpen}
        messages={messages}
        messageMentionFilter={messageMentionFilter}
        navigateToConferenceByConfId={navigateToConferenceByConfId}
        newestMessageOwnedByUserId={newestMessageOwnedByUserId}
        resolveConversationLinkPath={resolveConversationLinkPath}
        scroll={scroll}
        scrollBottom={scrollBottom}
        selectedMentionParticipantId={selectedMentionParticipantId}
        selectFilteredOtherParticipantPersonsInCurrentConversation={
          selectFilteredOtherParticipantPersonsInCurrentConversation
        }
        selectHasRecentActivityWithin={selectHasRecentActivityWithin}
        isBackfillingToReadMessage={isBackfillingToReadMessage}
        selectMarkedAsReadInfo={selectMarkedAsReadInfo}
        selectEpochMsSinceMarkedAsRead={selectEpochMsSinceMarkedAsRead}
        selectParticipantPersons={selectParticipantPersons}
        selectPersonPresenceStatus={selectPersonPresenceStatus}
        selectUnreadCounts={selectUnreadCounts}
        setConversationAndTotalUnreadCount={setConversationAndTotalUnreadCount}
        setEditMessageDraftHtml={setEditMessageDraftHtml}
        setEditMessageDraftRaw={setEditMessageDraftRaw}
        setEmojiPickerState={setEmojiPickerState}
        setIsEditingMessageId={setIsEditingMessageId}
        setMarkedAsReadInfo={setMarkedAsReadInfo}
        setMentionListOpen={setMentionListOpen}
        setMessageMentionFilter={setMessageMentionFilter}
        setSelectedMentionParticipantId={setSelectedMentionParticipantId}
        stayScrolled={stayScrolled}
        updateMyLastReadMessage={updateMyLastReadMessage}
        selectPersonMessageStatus={selectPersonMessageStatus}
        conversationStore={conversationStore}
        setCopiedConferenceId={ui.setCopiedConferenceId}
        copiedConferenceId={ui.copiedConferenceId}
        copyToClipboard={ui.copyToClipboard}
        ldClient={ldClient}
        messageIdToScroll={ui.messageIdToScroll}
        setMessageIdToScroll={ui.setMessageIdToScroll}
        loadPersonByIdGetIfMissingGet={loadPersonByIdGetIfMissingGet}
      />
    );
  });
  // ** REMEMBER: THIS SFC IS NOT AN OBSERVER **

  const allParticipants = selectParticipantsByConversationId(conversationId);

  return (
    <>
      <Styled.ContentItemsList
        data-private
        className="context-content-items-list"
        key="ccilp"
        ref={listRef}
        onScroll={onScroll}
        id="context-content-items-list"
        tabIndex={0}
        aria-label="Chat messages list"
      >
        {curConversation?.grouping !== 'Channel' &&
          curConversation?.grouping !== 'Group' &&
          allParticipants?.case({
            fulfilled: (resp) => {
              // Remove the PrintMessageHeader rendering logic
              // Since it's no longer needed, we can return null or remove this entire block
              return null;
            },
          })}
        {ccigNodes}
      </Styled.ContentItemsList>
      <ScrollToBottomFAB scrollContainerRef={listRef} />
    </>
  );
}
// ** REMEMBER: THIS SFC IS NOT AN OBSERVER **
export default ContextContentItemsList;

// ** SCROLL HANDLERS **
const scrollBulkhead = Policy.bulkhead(1, 1);
const onCCILScrollBulkhead = (
  ccilRef: React.MutableRefObject<HTMLDivElement>,
  manageLastScrollAndBackfill: IManageLastScrollAndBackfill,
  scroll: (scrollTop: number) => void
) => {
  return scrollBulkhead
    .execute(() => onCCILScroll(ccilRef, manageLastScrollAndBackfill, scroll))
    .then(
      (c) => c,
      (err) => {
        if (err instanceof BulkheadRejectedError) {
          console.warn(
            `onCCILScrollBulkhead overloaded with BulkheadRejectedError, try again.`,
            manageLastScrollAndBackfill
          );
        } else {
          console.error(
            `onCCILScrollBulkhead error (not BulkheadRejectedError)`,
            err,
            manageLastScrollAndBackfill
          );
          bugsnagClient.notify(err, (event) => {
            event.context = 'ContextContentItemsList';
            event.addMetadata('custom', { function: 'onCCILScrollBulkhead' });
          });
        }
        return err;
      }
    );
};

const lMCMBulkhead = Policy.bulkhead(1, 1);
const loadMoreConversationMessagesBulkhead = (
  manageLastScrollAndBackfill: IManageLastScrollAndBackfill,
  conversationId: string,
  msgs: GroupedMessagesContainer
) => {
  return lMCMBulkhead
    .execute(() =>
      manageLastScrollAndBackfill.loadMoreConversationMessages(
        conversationId,
        LOAD_DIRECTION.Older,
        msgs.OldestMessageId
      )
    )
    .then(
      (c) => c,
      (err) => {
        if (err instanceof BulkheadRejectedError) {
          console.warn(
            `loadMoreConversationMessagesBulkhead overloaded with BulkheadRejectedError, try again.`,
            manageLastScrollAndBackfill
          );
        } else {
          console.error(
            `loadMoreConversationMessagesBulkhead error (not BulkheadRejectedError)`,
            err,
            manageLastScrollAndBackfill
          );
          bugsnagClient.notify(err, (event) => {
            event.context = 'ContextContentItemsList';
            event.addMetadata('custom', {
              function: 'loadMoreConversationMessagesBulkhead',
            });
          });
        }
        return err;
      }
    );
};
/**
 * Execution callback for scrolling `ContextContentItemsList`.
 * Returns:
 * - If a possible scroll event occurred _OR_ manageLastScrollAndBackfill.selectCanLoadOlderMessages returned true, a `Promise` resolved with: `boolean` indicating whether the `LastScroll`'s values changed.
 * - Otherwise, a `Promise` resolved with `null`
 * - On failure, a rejected `Promise` containing the `Error`
 */
const onCCILScroll = (
  ccilRef: React.MutableRefObject<HTMLDivElement>,
  manageLastScrollAndBackfill: IManageLastScrollAndBackfill,
  scroll: (scrollTop: number) => void
) => {
  const conversationId = manageLastScrollAndBackfill.conversationId;
  const convLastScroll = toJS(
    manageLastScrollAndBackfill.getOrConstructConversationLastScroll(
      conversationId
    )
  );
  const msgs = manageLastScrollAndBackfill.getCurrentMessages(conversationId);
  const initialRefScroll = {
    scrollTop: ccilRef.current.scrollTop,
    scrollHeight: ccilRef.current.scrollHeight,
    clientHeight: ccilRef.current.clientHeight,
  };

  const scrollHeightDiff =
    convLastScroll.scrollTop - initialRefScroll.scrollTop;
  const lmmSkipIds =
    manageLastScrollAndBackfill.selectLoadMoreMessagesSkipIds(conversationId);
  // Backfill if needed
  if (
    msgs.OldestMessageId !== null &&
    convLastScroll.clientHeight !== 0 &&
    convLastScroll.scrollHeight !== 0 &&
    ccilRef.current.scrollTop <= ccilRef.current.clientHeight / 4 &&
    (lmmSkipIds === null || !lmmSkipIds.includes(msgs.OldestMessageId)) &&
    (ccilRef.current.scrollTop < convLastScroll.scrollTop ||
      (ccilRef.current.scrollTop === 0 && convLastScroll.scrollTop === 0)) &&
    manageLastScrollAndBackfill.selectCanLoadOlderMessages(
      manageLastScrollAndBackfill.conversationId
    )
  ) {
    manageLastScrollAndBackfill.setIsLoadingOlderMessages(conversationId, true);
    // if (NODE_ENV_LOCAL_OR_DEVELOPMENT) {
    //     console.debug(`onCCILScroll loadMoreConversationMessages for ${conversationId} || OldestMessageId ${msgs.OldestMessageId} || convLastScroll ${JSON.stringify(convLastScroll)}`);
    // }
    const lmmReq = loadMoreConversationMessagesBulkhead(
      manageLastScrollAndBackfill,
      conversationId,
      msgs
    );
    if (lmmReq) {
      return lmmReq.then(
        (res) => {
          return new Promise((resolve) => {
            setTimeout(() => {
              // Deferred to the next tick so the DOM has re-rendered with the
              // newly prepended (older) messages before we measure and restore.
              // Keep the viewport around the message that was previously at the
              // top: newScrollTop = oldScrollTop + (newScrollHeight - oldScrollHeight).
              const currentScrollHeight = ccilRef.current?.scrollHeight ?? 0;
              const currentClientHeight = ccilRef.current?.clientHeight ?? 0;
              const scrollTopAdjustment =
                currentScrollHeight - initialRefScroll.scrollHeight;
              let returnScroll =
                initialRefScroll.scrollTop + scrollTopAdjustment;
              if (returnScroll === 0) {
                returnScroll =
                  get(res, 'data.results.length', 0) > 0
                    ? currentClientHeight / 3
                    : 0;
              }
              try {
                scroll(returnScroll);
              } catch (e) {
                // just ignore the exception, this happens when switching back and forth quickly between conversations
                // this is due to the fact that this runs on the next frame, and the component might have been unmounted
              }
              // Persist the freshly measured heights so the next load
              // has an accurate baseline instead of a stale scrollHeight.
              const adjustedLs: ILastScroll = {
                clientHeight: currentClientHeight,
                scrollHeight: currentScrollHeight,
                scrollTop: ccilRef.current?.scrollTop ?? returnScroll,
              };
              manageLastScrollAndBackfill.setIsLoadingOlderMessages(
                conversationId,
                false
              );
              resolve(
                manageLastScrollAndBackfill.assignLastScroll(
                  conversationId,
                  adjustedLs
                )
              ); // whether the `LastScroll`'s values changed
            });
          });
        },
        (e) => {
          console.error(
            `onCCILScroll Error loading older Messages with error`,
            e
          );
          manageLastScrollAndBackfill.setIsLoadingOlderMessages(
            conversationId,
            false
          ); // reset
          return e;
        }
      );
    } else {
      return Promise.reject(
        new Error('loadMoreConversationMessages result was null or undefined')
      );
    }
  } else if (
    manageLastScrollAndBackfill.selectCanLoadOlderMessages(conversationId)
  ) {
    let scrollTop = initialRefScroll.scrollTop;
    if (scrollHeightDiff > 0) {
      manageLastScrollAndBackfill.userIsScrolling.current = true;
    }
    if (
      manageLastScrollAndBackfill.lastUnreadCount === 0 &&
      !manageLastScrollAndBackfill.scrolledToBottomOnFirstLoad &&
      scrollHeightDiff <= 0 &&
      !manageLastScrollAndBackfill.userIsScrolling.current
    ) {
      scrollTop = initialRefScroll.scrollHeight - initialRefScroll.clientHeight;
      scroll(scrollTop);
    }
    initialRefScroll.scrollTop = scrollTop;
    return Promise.resolve(
      manageLastScrollAndBackfill.assignLastScroll(
        conversationId,
        initialRefScroll
      )
    ); // whether the `LastScroll`'s values changed
  }
  return Promise.resolve(null); // Nothing happened
};
